import { useState } from 'react';
import { Link, NavLink } from 'react-router';
import type { AccountInfo } from '@sl/core';
import { Badge } from '../../ui/Badge/Badge';
import { Icon, ICON_NAMES, type IconName } from '../../ui/Icon';
import { Modal } from '../../ui/Modal/Modal';
import { cx } from '../../ui/cx';
import { BrandMark } from '../../routes/welcome/BrandMark';
import { BrandLogo } from '../brand/BrandLogo';
import { SEARCH_COPY, useSearchController } from '../../search';
import { testId } from '../testId';
import { useAppConfig } from '../configContext';
import { readPreviewIdentity } from '../../routes/acquisition/previewIdentity';
import { SurfaceModeChip } from './ModeChip';
// The ROUTING TABLE, not the manifest (T-321 / BUG-155): the nav needs the `nav` blocks and the routes'
// addresses, and importing the manifest for them put 2,300 lines of QA declaration in the first chunk
// (REQ-280 AC1 / AC3). The manifest is built from this table, so a destination still cannot exist without a
// route behind it — see the header of `routes.table.ts`.
import {
  navDestinations,
  routeSlug,
  tabBarDestinations,
  type NavDestination,
} from '../../routes.table';
import { SHELL_COPY } from './copy';
import { LogoutButton, useLogoutEvents } from './LogoutButton';
import styles from './AppShell.module.css';

// The navigation — DESIGN_SYSTEM §10. One list of destinations, three shapes by window width:
//   ≥ 1180 px   the 232 px expanded nav: brand, destinations, the footer group pinned to the bottom
//   768–1179    the same nav as a 64 px icon rail — labels become the item's `title` tooltip
//   < 768       no side nav at all: the bottom tab bar, with the `primary` destinations
// One `<nav aria-label="Main">` is rendered per shape and only one is displayed at a time (CSS), so a screen
// reader never hears the destinations twice — the hidden one is `display: none`, which removes it from the
// accessibility tree.
//
// THE DESTINATIONS COME FROM `routes.table.ts`. A destination cannot exist without a route behind it, so
// the nav can never show a dead link (DESIGN_SYSTEM §10: "MVP never shows a dead link"). Today the manifest
// carries Home; Campaigns, Spend & Budgets, Creatives, Alerts, Activity and Settings join the nav in the
// same breath as their screens — one `nav:` block each, no change here.

function iconOf(destination: NavDestination): IconName {
  const name = destination.nav.icon as IconName;
  if (import.meta.env.DEV && !ICON_NAMES.includes(name)) {
    throw new Error(
      `routes.table.ts: route "${destination.id}" asks for icon "${destination.nav.icon}", which ui/Icon does not have.`,
    );
  }
  return name;
}

function badgeCount(destination: NavDestination, account: AccountInfo): number | null {
  if (destination.nav.badge !== 'unreadAlerts') return null;
  return account.unreadAlerts > 0 ? account.unreadAlerts : null;
}

interface ItemProps {
  destination: NavDestination;
  account: AccountInfo;
  /** Tab-bar items show the short label and no tooltip. */
  compact?: boolean;
}

/** Exported for the badge contract test: no manifest entry asks for a badge until Alerts has a route. */
export function NavItem({ destination, account, compact = false }: ItemProps) {
  const { label, shortLabel } = destination.nav;
  const count = badgeCount(destination, account);
  return (
    <NavLink
      to={destination.path}
      className={({ isActive }) =>
        cx(compact ? styles.tabItem : styles.navItem, isActive && styles.isActive)
      }
      title={compact ? undefined : label}
      data-testid={testId('shell', 'nav', routeSlug(destination.id))}
    >
      <Icon name={iconOf(destination)} size={compact ? 24 : 16} />
      <span className={styles.navLabel}>{compact ? (shortLabel ?? label) : label}</span>
      {count === null ? null : (
        <Badge tone="alert" className={styles.navBadge} testID={testId('shell', 'alerts-badge')}>
          {count}
        </Badge>
      )}
    </NavLink>
  );
}

/**
 * The plan a user's row names. `demo` is not a plan anyone bought, so it says what the account IS
 * (design QA 8 with REQ-006: a demo never dresses itself up as a paying account).
 */
export function planLabel(planKey: string): string {
  if (planKey === 'demo') return 'Sample account';
  // BUG-136: a key is snake_case on the wire (`free_trial`); the rail says the plan's name in words.
  const words = planKey.replace(/_/g, ' ').trim();
  return `${words.charAt(0).toUpperCase()}${words.slice(1)} plan`;
}

/** "Maya Reyes" → "MR". Decorative: the name is beside it. */
function userInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return (
    words
      .slice(0, 2)
      .map((w) => w[0] ?? '')
      .join('')
      .toUpperCase() || '?'
  );
}

export interface ShellNavProps {
  account: AccountInfo;
  /** Who is signed in, for the row at the foot of the nav (DESIGN_SYSTEM §10). */
  user: { displayName: string; id?: string; email?: string };
}

/** The side nav: expanded above 1180 px, a 64 px icon rail from 768 px. Hidden below 768 px. */
export function ShellNav({ account, user }: ShellNavProps) {
  const main = navDestinations('main');
  const footer = navDestinations('footer');
  return (
    <nav
      className={styles.nav}
      aria-label={SHELL_COPY.navLabel}
      data-testid={testId('shell', 'nav')}
    >
      <span className={styles.brand}>
        {/* The wordmark (D-1268); in the icon rail (≤ 1179, the stylesheet) the "Ad" mark stands in for it. */}
        <BrandLogo className={styles.brandLogo} height={26} />
        <span className={styles.brandMark}>
          <BrandMark />
        </span>
      </span>
      {main.map((destination) => (
        <NavItem key={destination.id} destination={destination} account={account} />
      ))}
      <span className={styles.navSpacer} />
      {footer.map((destination) => (
        <NavItem key={destination.id} destination={destination} account={account} />
      ))}
      <UserRow user={user} planKey={account.limits.planKey} />
      <LogoutButton member={/^usr_[0-9a-f]{32}$/.test(user.id ?? '')} />
    </nav>
  );
}

/**
 * The person stays visible even on sample ad data. The row opens their Facebook account setup.
 * In the compact rail, the avatar and accessible name retain the same destination.
 */
export function UserRow({
  user,
  planKey,
}: {
  user: { displayName: string; id?: string; email?: string };
  planKey: string;
}) {
  const { apiBuild } = useAppConfig();
  const preview = apiBuild ? null : readPreviewIdentity();
  const displayName =
    preview?.displayName.trim() ||
    preview?.email.trim() ||
    (user.displayName?.trim() !== 'You' ? user.displayName?.trim() : '') ||
    user.email?.trim() ||
    'Your account';
  const destination = apiBuild ? '/account' : preview ? '/account?preview=1' : '/signup?preview=1';
  const content = (
    <>
      <span className={styles.navUserAvatar} aria-hidden="true">
        {userInitials(displayName)}
      </span>
      <span className={styles.navUserText}>
        <span className={styles.navUserName}>{displayName}</span>
        <span className={styles.navUserPlan}>{planLabel(planKey)}</span>
        <span className={styles.navUserAction}>Manage accounts</span>
      </span>
      <Icon name="chevron-right" size={16} className={styles.navUserChevron} />
    </>
  );
  return (
    <Link
      to={destination}
      className={styles.navUser}
      data-testid={testId('shell', 'user')}
      aria-label={`${displayName} — Manage Facebook accounts`}
      title={`${displayName} — Manage Facebook accounts`}
    >
      {content}
    </Link>
  );
}

/**
 * The phone tab bar below 768 px (REQ-282 AC1, DESIGN_SYSTEM §10 / §11, REQ-240 AC5):
 * **Home · Campaigns · Spend · Alerts · More**, exactly as the phone app has it.
 *
 * The fifth slot is not a destination. There is no side nav down here, so Creatives, Activity and Settings
 * would otherwise have no way in at all — before T-043 the slot held Settings and the other two were
 * unreachable (design QA B3). "More" opens a sheet listing every nav destination the tab bar does not
 * carry, read from the SAME manifest the nav is built from, so it can never show a dead link and can never
 * fall behind the nav. It is a sheet rather than a sixth route because the list has no content of its own:
 * it is navigation, and navigation belongs in the chrome.
 */
export function ShellTabBar({ account, user }: ShellNavProps) {
  // Keep the browser's session listener active while the More sheet is closed.
  useLogoutEvents(/^usr_[0-9a-f]{32}$/.test(user.id ?? ''));
  const [moreOpen, setMoreOpen] = useState(false);
  const search = useSearchController();
  const inTabBar = new Set(tabBarDestinations().map((d) => d.id));
  const rest = navDestinations().filter((d) => !inTabBar.has(d.id));
  return (
    <>
      <nav
        className={styles.tabbar}
        aria-label={SHELL_COPY.navLabel}
        data-testid={testId('shell', 'tabbar')}
      >
        {tabBarDestinations().map((destination) => (
          <NavItem key={destination.id} destination={destination} account={account} compact />
        ))}
        {rest.length > 0 ? (
          <button
            type="button"
            className={cx(styles.tabItem, moreOpen && styles.isActive)}
            aria-expanded={moreOpen}
            aria-haspopup="dialog"
            data-testid={testId('shell', 'nav', 'more')}
            onClick={() => setMoreOpen(true)}
          >
            <Icon name="menu" size={24} />
            <span className={styles.navLabel}>{SHELL_COPY.more}</span>
          </button>
        ) : null}
      </nav>
      <Modal
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        title={SHELL_COPY.moreTitle}
        subtitle={SHELL_COPY.moreSubtitle}
        size="sm"
        testID={testId('shell', 'more-sheet')}
        /* REQ-006 AC5 / BUG-009: at 360 px this sheet IS the screen, so it repeats the mode in its own
           header rather than leaving an owner looking at a covered chip. */
        modeChip={<SurfaceModeChip testID={testId('shell', 'more-sheet', 'mode-chip')} />}
      >
        <ul className={styles.moreList}>
          {/* T-060's `pointer.search`: the phone app sends an owner to the web for global search, so the web
              at 360 px has to carry it properly. The top bar's icon is a 44 px target already; this is the
              second way in, where someone looking for "everything else" will go. */}
          {search ? (
            <li>
              <button
                type="button"
                className={styles.moreItem}
                data-testid={testId('search', 'trigger', 'more')}
                onClick={() => {
                  setMoreOpen(false);
                  search.openSearch();
                }}
              >
                <span className={styles.moreIcon}>
                  <Icon name="search" size={20} />
                </span>
                <span className={styles.moreLabel}>{SEARCH_COPY.trigger}</span>
                <Icon name="chevron-right" size={16} className={styles.moreChevron} />
              </button>
            </li>
          ) : null}
          {rest.map((destination) => (
            <li key={destination.id}>
              <NavLink
                to={destination.path}
                className={styles.moreItem}
                data-testid={testId('shell', 'more', routeSlug(destination.id))}
                onClick={() => setMoreOpen(false)}
              >
                <span className={styles.moreIcon}>
                  <Icon name={iconOf(destination)} size={20} />
                </span>
                <span className={styles.moreLabel}>{destination.nav.label}</span>
                <Icon name="chevron-right" size={16} className={styles.moreChevron} />
              </NavLink>
            </li>
          ))}
        </ul>
        {/* §10 puts a user row at the foot of the nav; below 768 px there is no nav, so it lives here
            (T-043's note (3)). */}
        <UserRow user={user} planKey={account.limits.planKey} />
        <LogoutButton member={/^usr_[0-9a-f]{32}$/.test(user.id ?? '')} />
      </Modal>
    </>
  );
}
